import { useAtom } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { useStoneFindingsQuery } from "../../hooks/useStoneFindingsQuery";
import {
  selectedStoneIdAtom,
  stoneAttrEditsAtom,
  stoneVerdictsAtom,
} from "../../atoms/stoneReviewAtoms";
import { computeBurden, effectiveStone } from "../../stones/burden";
import { composeStoneFinding } from "../../stones/composeStoneFinding";
import { renderStoneThumbnail } from "../../stones/stoneThumbnail";
import { buildStoneEvent, emitStoneEvent } from "../../stones/stoneTelemetry";
import type { StoneFinding, StoneLaterality, StoneLocation } from "../../stones/types";
import { StoneCard } from "./StoneCard";

export interface StoneRailProps {
  studyUid: string;
  onStoneSelect: (stone: StoneFinding) => void; // → CaseViewer runs selectFinding
  onShowReportTab: () => void; // flips rightSidebarTab to "report"
  loadFrame: (
    seriesUid: string,
    sliceIndex: number,
    window: { windowWidth: number; windowCenter: number },
  ) => Promise<HTMLCanvasElement>;
  // Reserved for the durable review workflow. The fixture-only surface does
  // not expose save/advance until an authenticated write endpoint exists.
  onAdvance?: () => void;
  hasNext?: boolean;
}

const TAB_LABEL_CLASS = "font-mono text-[10px] uppercase tracking-[0.14em] px-3 py-2";

function GhostRow({ i }: { i: number }) {
  return (
    <div
      data-testid="stone-rail-ghost"
      className="flex gap-3 p-2.5 bg-surface border border-border rounded-[2px]"
    >
      <div className="shrink-0 w-[132px] h-[132px] rounded-[2px] bg-bg-2 animate-pulse" />
      <div className="flex-1 flex flex-col gap-1.5 pt-0.5">
        <div className="h-2.5 bg-bg-2 rounded-[2px] w-1/3 animate-pulse" />
        <div className="h-3 bg-bg-2 rounded-[2px] w-2/3 animate-pulse" />
        <div className="h-3 bg-bg-2 rounded-[2px] w-1/2 animate-pulse" />
        <div className="h-6 bg-bg-2 rounded-[2px] w-1/3 mt-auto animate-pulse" />
      </div>
      <span className="sr-only">loading stone {i}</span>
    </div>
  );
}

/** Right-rail container (Variant C): tab strip, burden summary, stone-card
 * list (with per-id thumbnail rendering), and a pinned composed report line. */
export function StoneRail({ studyUid, onStoneSelect, onShowReportTab, loadFrame }: StoneRailProps) {
  const { stones, isLoading, error } = useStoneFindingsQuery(studyUid);
  const [verdicts, setVerdicts] = useAtom(stoneVerdictsAtom);
  const [edits, setEdits] = useAtom(stoneAttrEditsAtom);
  const [selectedId, setSelectedId] = useAtom(selectedStoneIdAtom);
  const [thumbnails, setThumbnails] = useState<Record<string, HTMLCanvasElement | null>>({});

  const [sortOrder, setSortOrder] = useState<"size" | "slice">("size");

  useEffect(() => {
    if (error) console.error("[stone-rail] failed to load stone findings", error);
  }, [error]);

  const sorted = useMemo(
    () =>
      [...stones].sort((a, b) =>
        sortOrder === "size"
          ? b.measurements.longAxisMm - a.measurements.longAxisMm
          : a.sliceIndex - b.sliceIndex,
      ),
    [stones, sortOrder],
  );
  const rows = useMemo(
    () => sorted.map((s) => ({ raw: s, eff: effectiveStone(s, edits[s.id]) })),
    [sorted, edits],
  );

  // Thumbnails don't depend on location/laterality edits (crop + calipers are
  // geometry-only), so re-fetch only when the stone set or loader changes —
  // not on every attribute edit.
  useEffect(() => {
    let cancelled = false;
    for (const stone of sorted) {
      renderStoneThumbnail(stone, loadFrame)
        .then((canvas) => {
          if (!cancelled) setThumbnails((prev) => ({ ...prev, [stone.id]: canvas }));
        })
        .catch((err) => console.error("[stone-thumbnail] render failed", err));
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorted, loadFrame]);

  const burden = computeBurden(stones, verdicts);

  const handleSelect = (id: string) => {
    setSelectedId(id);
    const row = rows.find((r) => r.raw.id === id);
    if (row) onStoneSelect(row.eff);
  };
  const emitVerdict = (id: string, verdict: "approved" | "denied") => {
    const stone = stones.find((s) => s.id === id);
    if (!stone) return;
    emitStoneEvent(
      buildStoneEvent("stone_verdict", {
        stoneId: id,
        verdict,
        confidence: stone.confidence,
        longAxisMm: stone.measurements.longAxisMm,
        huMax: stone.hu.max,
      }),
    );
  };
  const handleApprove = (id: string) => {
    setVerdicts((v) => ({ ...v, [id]: "approved" }));
    emitVerdict(id, "approved");
  };
  const handleDeny = (id: string) => {
    setVerdicts((v) => ({ ...v, [id]: "denied" }));
    emitVerdict(id, "denied");
  };
  const handleLocationChange = (id: string, loc: StoneLocation) => {
    const from = rows.find((r) => r.raw.id === id)?.eff.location ?? null;
    setEdits((e) => ({ ...e, [id]: { ...e[id], location: loc } }));
    emitStoneEvent(
      buildStoneEvent("stone_attr_edit", { stoneId: id, field: "location", from, to: loc }),
    );
  };
  const handleLateralityChange = (id: string, laterality: StoneLaterality) => {
    const from = rows.find((r) => r.raw.id === id)?.eff.laterality ?? null;
    setEdits((e) => ({ ...e, [id]: { ...e[id], laterality } }));
    emitStoneEvent(
      buildStoneEvent("stone_attr_edit", {
        stoneId: id,
        field: "laterality",
        from,
        to: laterality,
      }),
    );
  };

  const approvedStones = rows
    .filter((r) => (verdicts[r.raw.id] ?? "pending") === "approved")
    .map((r) => r.eff);
  const composed = composeStoneFinding(approvedStones);

  return (
    <div className="flex flex-col h-full bg-bg-2 text-fg overflow-hidden">
      <div className="flex shrink-0 border-b border-border">
        <div className={`relative ${TAB_LABEL_CLASS} text-fg`}>
          Stones · {stones.length}
          <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-signal" />
        </div>
        <button
          type="button"
          onClick={onShowReportTab}
          className={`${TAB_LABEL_CLASS} text-fg-muted hover:text-fg transition-colors`}
        >
          Report
        </button>
      </div>

      {!isLoading && !error && (
        <div className="shrink-0 flex items-center justify-between gap-2 px-3 py-2 border-b border-border font-mono text-[10px] text-fg-muted">
          <span>
            {burden.detected} detected · {burden.approved} approved · {burden.denied} denied ·
            largest {burden.largestMm} mm · &Sigma; {burden.sumLongAxisMm} mm
          </span>
          <select
            aria-label="Sort stones"
            data-testid="stone-sort"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as "size" | "slice")}
            className="appearance-none shrink-0 font-mono text-[10px] uppercase tracking-wide bg-surface-2 border border-border-strong rounded-[2px] px-1.5 py-0.5 text-fg-muted cursor-pointer focus:outline-none focus:border-border-strong"
          >
            <option value="size">Size</option>
            <option value="slice">Slice</option>
          </select>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-2">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => <GhostRow key={i} i={i} />)
        ) : error ? (
          <div role="alert" className="px-3 py-8 text-center font-mono text-xs text-fg-muted">
            Couldn&apos;t load stones
          </div>
        ) : stones.length === 0 ? (
          <div className="px-3 py-8 text-center font-mono text-xs text-fg-muted">
            No renal stones detected
          </div>
        ) : (
          rows.map(({ raw, eff }) => (
            <StoneCard
              key={raw.id}
              stone={eff}
              verdict={verdicts[raw.id] ?? "pending"}
              selected={selectedId === raw.id}
              thumbnail={thumbnails[raw.id] ?? null}
              onSelect={handleSelect}
              onApprove={handleApprove}
              onDeny={handleDeny}
              onLocationChange={handleLocationChange}
              onLateralityChange={handleLateralityChange}
            />
          ))
        )}
      </div>

      <div className="shrink-0 px-3 py-2 border-t border-border font-mono text-[11px] text-fg">
        {composed.text || "No stones approved yet — approve a stone to build this line."}
      </div>

      {!isLoading && !error && stones.length > 0 && (
        <div className="shrink-0 px-3 py-2.5 border-t border-border font-mono text-[10px] text-fg-muted">
          Prototype preview · review saving unavailable
        </div>
      )}
    </div>
  );
}
